import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { BadgeNotificationModal } from "../components/UserBadges";
import CustomerLayout from "../components/CustomerLayout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Globe, ExternalLink, Clock, CheckCircle, AlertCircle,
  TrendingUp, Zap, Sparkles, Link2,
} from "lucide-react";
import FloatingCart from "../components/FloatingCart";

const CustomerDashboard = () => {
  const { user, clearBadgeNotifications } = useAuth();
  const { language } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [seoOrders, setSeoOrders] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBadgeNotification, setShowBadgeNotification] = useState(false);
  const [pendingBadges, setPendingBadges] = useState([]);

  const [linkStats, setLinkStats] = useState({ totalLinks: 0, activeLinks: 0 });
  const [groupedLinks, setGroupedLinks] = useState([]);
  const [expandedUrl, setExpandedUrl] = useState(null);
  const [linkDetailsAll, setLinkDetailsAll] = useState({});
  const [expandedPage, setExpandedPage] = useState({});
  const [expandedLoading, setExpandedLoading] = useState(false);
  const LINKS_PER_PAGE = 5;

  useEffect(() => {
    if (user?.pending_badge_notifications?.length > 0) {
      setPendingBadges(user.pending_badge_notifications);
      setShowBadgeNotification(true);
    }
  }, [user?.pending_badge_notifications]);

  const handleCloseBadgeNotification = async () => {
    setShowBadgeNotification(false);
    setPendingBadges([]);
    await clearBadgeNotifications();
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const [ordersRes, seoOrdersRes, announcementsRes, linksRes] = await Promise.all([
          api.get("/orders?limit=1000").catch(() => ({ data: { orders: [] } })),
          api.get("/seo-orders?limit=1000").catch(() => ({ data: { orders: [] } })),
          api.get("/announcements?limit=5").catch(() => ({ data: { announcements: [] } })),
          api.get("/my-links?page=1&limit=20").catch(() => ({ data: { total_links: 0, total_active: 0, grouped_links: [] } })),
        ]);

        if (isMounted) {
          setOrders(ordersRes.data.orders || []);
          setSeoOrders(seoOrdersRes.data.orders || []);
          setAnnouncements(announcementsRes.data.announcements || []);
          setLinkStats({
            totalLinks: linksRes.data.total_links || 0,
            activeLinks: linksRes.data.total_active || 0,
          });
          setGroupedLinks(linksRes.data.grouped_links || []);
          setLoading(false);
        }
      } catch (error) {
        console.error("Fetch error:", error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAnnouncementTypeStyles = (type) => {
    switch (type) {
      case "warning": return "bg-yellow-500/20 border-yellow-500/50 text-yellow-200";
      case "error": return "bg-red-500/20 border-red-500/50 text-red-200";
      case "success": return "bg-green-500/20 border-green-500/50 text-green-200";
      default: return "bg-blue-500/20 border-blue-500/50 text-blue-200";
    }
  };

  const getStatusBadge = (status) => {
    return <span className="status-active px-2 py-1 text-xs font-mono">{language === "tr" ? "AKTİF" : "ACTIVE"}</span>;
  };

  const toggleExpand = async (targetUrl) => {
    if (expandedUrl === targetUrl) {
      setExpandedUrl(null);
      return;
    }
    setExpandedUrl(targetUrl);
    if (linkDetailsAll[targetUrl]) {
      setExpandedPage((prev) => ({ ...prev, [targetUrl]: 1 }));
      return;
    }
    setExpandedLoading(true);
    try {
      const res = await api.get(`/my-links/${encodeURIComponent(targetUrl)}`);
      const links = res.data.links || [];
      setLinkDetailsAll((prev) => ({ ...prev, [targetUrl]: links }));
      setExpandedPage((prev) => ({ ...prev, [targetUrl]: 1 }));
    } catch (e) {
      console.error("Link detayları yüklenemedi:", e);
      toast.error(language === "tr" ? "Linkler yüklenemedi" : "Failed to load links");
    } finally {
      setExpandedLoading(false);
    }
  };

  const setExpandedPageFor = (targetUrl, page) => {
    setExpandedPage((prev) => ({ ...prev, [targetUrl]: page }));
  };

  return (
    <>
      {showBadgeNotification && (
        <BadgeNotificationModal
          badges={pendingBadges}
          language={language}
          onClose={handleCloseBadgeNotification}
        />
      )}

      <CustomerLayout activeId="dashboard">
        <div className="p-6">
          <div className="max-w-6xl mx-auto">
            {announcements.length > 0 && (
              <div className="mb-6">
                <h2 className="text-lg font-mono font-bold mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  {language === "tr" ? "DUYURULAR" : "ANNOUNCEMENTS"}
                </h2>
                <div className="space-y-2">
                  {announcements.slice(0, 3).map((announcement) => (
                    <div
                      key={announcement.id}
                      className={`p-4 rounded-lg border ${getAnnouncementTypeStyles(announcement.type)}`}
                    >
                      <div className="flex items-start gap-3">
                        <div>
                          <h4 className="font-mono font-bold text-sm">
                            {language === "tr" ? announcement.title : (announcement.title_en || announcement.title)}
                          </h4>
                          <p className="text-sm mt-1 opacity-90">
                            {language === "tr" ? announcement.content : (announcement.content_en || announcement.content)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-mono font-bold uppercase">Dashboard</h1>
                <p className="text-muted-foreground mt-1">
                  {language === "tr" ? "Backlink ve SEO siparişlerinizi takip edin" : "Track your backlink and SEO orders"}
                </p>
              </div>
              <Link to="/sites">
                <Button className="font-mono uppercase bg-primary text-black hover:bg-primary/90 rounded-none">
                  <Zap className="w-4 h-4 mr-2" /> {language === "tr" ? "YENİ BACKLINK" : "NEW BACKLINK"}
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="glass p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Link2 className="w-4 h-4 text-primary" />
                  <span className="text-xs font-mono uppercase">{language === "tr" ? "Toplam Link" : "Total Links"}</span>
                </div>
                <div className="text-2xl font-mono font-bold text-primary">{linkStats.totalLinks}</div>
              </div>
              <div className="glass p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span className="text-xs font-mono uppercase">{language === "tr" ? "Aktif Link" : "Active Links"}</span>
                </div>
                <div className="text-2xl font-mono font-bold text-primary">{linkStats.activeLinks}</div>
              </div>
              <div className="glass p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  <span className="text-xs font-mono uppercase">{language === "tr" ? "SEO Sipariş" : "SEO Orders"}</span>
                </div>
                <div className="text-2xl font-mono font-bold text-primary">{seoOrders.length}</div>
              </div>
              <div className="glass p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Clock className="w-4 h-4 text-primary" />
                  <span className="text-xs font-mono uppercase">{language === "tr" ? "Abonelik" : "Subscription"}</span>
                </div>
                <div className="text-sm font-mono font-bold text-primary">
                  {user?.is_subscribed && user?.subscription_end_date && new Date(user.subscription_end_date) > new Date()
                    ? (language === "tr" ? "Aktif" : "Active")
                    : (language === "tr" ? "Pasif" : "Inactive")}
                </div>
              </div>
            </div>

            <div className="mb-8 max-w-[90%] mx-auto">
              <div className="p-4 border-b border-white/10 mb-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-mono font-bold">{language === "tr" ? "Backlink Siparişlerim" : "My Backlink Orders"}</h3>
                  {groupedLinks.length > 0 && (
                    <Link to="/my-links">
                      <Button variant="outline" size="sm" className="font-mono rounded-none border-white/20 text-muted-foreground hover:text-foreground">
                        {language === "tr" ? "Tüm linkler" : "All links"} <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
              {loading ? (
                <div className="p-8 text-center text-muted-foreground font-mono">{language === "tr" ? "Yükleniyor..." : "Loading..."}</div>
              ) : groupedLinks.length === 0 ? (
                <div className="p-8 text-center glass rounded-lg">
                  <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground font-mono mb-4">{language === "tr" ? "Henüz backlink siparişiniz yok" : "No backlink orders yet"}</p>
                  <Link to="/sites">
                    <Button className="font-mono bg-primary text-black rounded-none">{language === "tr" ? "SİTELERİ İNCELE" : "BROWSE SITES"}</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {groupedLinks.map((group) => {
                    const isExpanded = expandedUrl === group.target_url;
                    const allLinks = linkDetailsAll[group.target_url] || [];
                    const page = expandedPage[group.target_url] || 1;
                    const totalPages = Math.ceil(allLinks.length / LINKS_PER_PAGE) || 1;
                    const pageLinks = allLinks.slice((page - 1) * LINKS_PER_PAGE, page * LINKS_PER_PAGE);
                    return (
                      <div key={group.target_url} className="glass overflow-hidden">
                        <div className="p-4">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div className="w-10 h-10 bg-white/10 rounded flex items-center justify-center flex-shrink-0">
                              <Globe className="w-5 h-5 text-primary" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-mono font-bold text-lg truncate">{group.target_url}</div>
                              <div className="text-sm text-muted-foreground">
                                <span className="text-green-400">{group.total_links} {language === "tr" ? "aktif" : "active"}</span>
                                {" / "}
                                {group.total_links} {language === "tr" ? "toplam" : "total"}
                              </div>
                              {group.keywords && group.keywords.length > 0 && (
                                <div className="text-xs text-primary/90 mt-1 flex flex-wrap gap-1">
                                  <span className="text-muted-foreground">{language === "tr" ? "Keywordler:" : "Keywords:"}</span>
                                  {group.keywords.map((kw, i) => (
                                    <span key={i} className="font-mono px-1.5 py-0.5 bg-primary/10 rounded">{kw}</span>
                                  ))}
                                </div>
                              )}
                              {group.last_order_date && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {language === "tr" ? "Son Sipariş:" : "Last order:"} {new Date(group.last_order_date).toLocaleDateString(language === "tr" ? "tr-TR" : "en-US")}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleExpand(group.target_url)}
                              className="flex-shrink-0 text-primary hover:text-primary/80 font-mono text-sm"
                            >
                              {isExpanded ? (language === "tr" ? "Kapat" : "Close") : (language === "tr" ? "Detay" : "Details")}
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="border-t border-white/10 p-4 bg-black/30">
                            {expandedLoading && allLinks.length === 0 ? (
                              <div className="text-center text-muted-foreground font-mono py-6">{language === "tr" ? "Yükleniyor..." : "Loading..."}</div>
                            ) : pageLinks.length === 0 ? (
                              <div className="text-center text-muted-foreground font-mono py-4">{language === "tr" ? "Link yok" : "No links"}</div>
                            ) : (
                              <>
                                <div className="space-y-3">
                                  {pageLinks.map((link, idx) => (
                                    <div key={link.id || idx} className="flex items-center justify-between p-3 bg-white/5 rounded">
                                      <div className="flex-1 min-w-0">
                                        <a href={link.site_url} target="_blank" rel="noopener noreferrer" className="font-mono text-sm text-primary hover:underline truncate flex items-center gap-1">
                                          {link.site_name || link.site_url}
                                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                        </a>
                                        <div className="text-xs text-muted-foreground mt-1">
                                          Keyword: <span className="text-white">{link.anchor_text}</span>
                                          {" · "}
                                          {language === "tr" ? "Bitiş:" : "Expires:"} {link.expires_at ? new Date(link.expires_at).toLocaleDateString(language === "tr" ? "tr-TR" : "en-US") : "-"}
                                        </div>
                                      </div>
                                      {getStatusBadge(link.status)}
                                    </div>
                                  ))}
                                </div>
                                {totalPages > 1 && (
                                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                                    <span className="text-xs text-muted-foreground font-mono">
                                      {language === "tr" ? "Sayfa" : "Page"} {page} / {totalPages} ({allLinks.length} {language === "tr" ? "toplam" : "total"})
                                    </span>
                                    <div className="flex gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 font-mono rounded-none border-white/20"
                                        onClick={() => setExpandedPageFor(group.target_url, page - 1)}
                                        disabled={page <= 1}
                                      >
                                        ←
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 font-mono rounded-none border-white/20"
                                        onClick={() => setExpandedPageFor(group.target_url, page + 1)}
                                        disabled={page >= totalPages}
                                      >
                                        →
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {seoOrders.length > 0 && (
              <div className="glass overflow-hidden">
                <div className="p-4 border-b border-white/10">
                  <h3 className="font-mono font-bold">{language === "tr" ? "SEO Siparişlerim" : "My SEO Orders"}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/5">
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">Hizmet</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">URL</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">Kredi</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">Durum</th>
                        <th className="text-left p-4 font-mono text-xs uppercase text-muted-foreground">Tarih</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seoOrders.slice(0, 5).map((order) => (
                        <tr key={order.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="p-4 font-mono font-bold">{order.service_name}</td>
                          <td className="p-4 text-sm text-muted-foreground truncate max-w-[200px]">{order.target_url}</td>
                          <td className="p-4 font-mono">{order.credit_cost}</td>
                          <td className="p-4">{getStatusBadge(order.status)}</td>
                          <td className="p-4 font-mono text-sm text-muted-foreground">
                            {new Date(order.created_at).toLocaleDateString("tr-TR")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </CustomerLayout>

      <FloatingCart />
    </>
  );
};

export default CustomerDashboard;
